import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import BasicTable from './basicTable';
import { CAMPAIGNS_TABLE, SELECTED_TRACKING_SOURCE_OPTION } from '../../constants/pageIdentifiers';
import { DEFAULT_ROWS_PER_PAGE, eCommerceExtendedColumns, extendedColumns } from '../../constants/constants';
import BasicDialog from '../../dialogs/basicDialog';
import DateCell from '../../partials/dateCell';
import { CampaignInterface } from '../../interfaces/campaign.interface';
import { useDeleteCampaign } from '../../apiHooks/mutations/adManagementMutations/campaignsMutations';
import { campaignFormInputs } from '../../constants/modalInputs/campaignFormInputs';
import CampaignsForm from '../../forms/campaignsForm';
import useGetCampaigns from '../../apiHooks/queries/adManagement/useGetCampaigns';
import TrackingSourceSelect from '../dropdownSelects/trackingSourceSelect';
import { useGetTrackingSourceById } from '../../apiHooks/queries/adManagement/useGetTrackingSources';
import { useAdManagementFilterDateStore, useDashboardFilterDateStore } from '../../store/dateRangeStore';
import { useOrganizationSettingsStore } from '../../store/organizationSettingsStore';
import OrganizationType from '../enums/organizationTypesEnum';
import { useGetExportCampaignsTable } from '../../apiHooks/mutations/reportsExportMutations/useGetExportCampaignsTable';
import ConfirmDialog from '../../dialogs/confirmDialog';
import CampaignCustomFieldSelect from "../CampaignCustomFieldSelect/campaignCustomFieldSelect";
import useGetCustomFieldSelectData from '../../apiHooks/queries/customFieldSelectData/useGetCustomFieldSelectData';

const columns: TableColumnInterface[] = [
  { field: 'name', headerName: 'name' },
  { field: 'trackingSource.name', headerName: 'trackingSource' },
  { field: 'campaignCode', headerName: 'code' },
  { field: 'conversionValue', headerName: 'conversionValue' },
  {
    field: 'createdAt',
    headerName: 'createdAt',
    cellRenderer: (params: string) => <DateCell params={params} />,
  },
];

const sortablesList = ['code', 'name', 'clicks', 'sales', 'lostIncome', 'earnedIncome', 'potentialIncome',
  'interactions', 'interactionsRate', 'impressions', 'createdAt', 'cost', 'netIncome', 'averageCpc'];

interface CampaignsTableProps {
  selectedTrackingSourceId: string;
  setSelectedTrackingSourceId: (_newselectedTrackingSourceId: string) => void;
}

const CampaignsTable: FC<CampaignsTableProps> = (props) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [editData, setEditData] = useState<CampaignInterface>();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedCustomFieldValues, setSelectedCustomFieldValues] = useState<Record<string, string[]>>({});
  const [isDeleting, setIsDeleting] = useState(false);
  const { mutateAsync: deleteCampaign } = useDeleteCampaign();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || '');
  const [orderBy, setOrderBy] = useState<string>(searchParams.get('orderBy') || '');
  const organizationType = useOrganizationSettingsStore((state) => state.organizationSettings?.organizationType);
  const canPinRows = (_id: number) => true;

  const [searchInput, setSearchInput] = useState({
    name: searchParams.get('name') || '',
  });
  const { t } = useTranslation();

  // TODO: Why is this on the component level ?
  localStorage.setItem('from', searchParams.get('adManagementFilterDateFrom') || '');
  localStorage.setItem('to', searchParams.get('adManagementFilterDateTo') || '');

  const {
    data: selectedTrackingSourceData,
  } = useGetTrackingSourceById(SELECTED_TRACKING_SOURCE_OPTION, props.selectedTrackingSourceId);

  const adManagementFilterDateRange = useDashboardFilterDateStore(
    (state) => state.dashboardFilterDateRange,
  );
  const setAdManagementFilterDateRange = useDashboardFilterDateStore(
    (state) => state.setDashboardFilterDateRange,
  );

  const customFieldValue = Object.entries(selectedCustomFieldValues)
    .filter(([, values]) => values.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([field, values]) => `${field}:${[...values].sort().join(',')}`)
    .join('|');

  const {
    data: campaignsData,
    isError,
    refetch: refetchCampaignsData,
    isFetching,
  } = useGetCampaigns(
    CAMPAIGNS_TABLE,
    searchInput.name,
    props.selectedTrackingSourceId,
    adManagementFilterDateRange[0],
    adManagementFilterDateRange[1],
    sortBy,
    orderBy,
    rowsPerPage,
    page + 1,
    customFieldValue,
  );

  const { mutate: fetchDownload } = useGetExportCampaignsTable(
    {
      name: searchInput.name,
      trackingSourceId: props.selectedTrackingSourceId,
      from: adManagementFilterDateRange[0],
      to: adManagementFilterDateRange[1],
      sortBy,
      orderBy,
      rows: rowsPerPage,
      page: page + 1,
    },
  );

  useEffect(() => {
    setPage(0);
  }, [adManagementFilterDateRange, sortBy, orderBy, props.selectedTrackingSourceId, selectedCustomFieldValues]);

  const handleOnDelete = async (id: number, name?:string) => {
    setIsDeleting(true);
    await deleteCampaign(id);
    await refetchCampaignsData();
    setIsDeleting(false);
  };

  const closeEditDialogAndRefetch = async () => {
    setIsEditDialogOpen(false);
    await refetchCampaignsData();
  };

  const handleGoToAdGroups = (campaign: any) => {
    navigate(`/ad-management/ad-groups?campaignId=${campaign.id}&campaignCode=${campaign.campaignCode}`);
  };
  const [isAreYouSureDialogOpen, setIsAreYouSureDialogOpen] = useState(false);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [selectedAdGroupName, setSelectedCampaignName] = useState<string>('');

  const handleDeleteClick = (id: number, name: string, campaignCode: string) => {
    setSelectedCampaignId(id);
    setSelectedCampaignName(name);
    setIsAreYouSureDialogOpen(true);
  };

  const { data: customFieldSelectData } = useGetCustomFieldSelectData('CUSTOM_FIELD_SELECT_DATA');

  const data = (customFieldSelectData?.data?.data.values
    && Object.keys(customFieldSelectData.data.data.values).length > 0
    ? customFieldSelectData.data.data.values
    : {}) as Record<string, string[]>;

  const handleCustomFieldSelectChange = useCallback((result: { field: string | null; values: string[] }) => {
    if (!result.field) return;

    const normalizedValues = Array.from(new Set(result.values ?? [])).filter(Boolean).sort();

    setSelectedCustomFieldValues((prev) => {
      const prevValues = prev[result.field as string] ?? [];
      const prevNormalized = Array.from(new Set(prevValues)).filter(Boolean).sort();

      if (prevNormalized.length === normalizedValues.length
        && prevNormalized.every((v, i) => v === normalizedValues[i])) {
        return prev;
      }

      if (normalizedValues.length === 0) {
        if (!(result.field as string in prev)) return prev;
        const next = { ...prev };
        delete next[result.field as string];
        return next;
      }

      return { ...prev, [result.field as string]: normalizedValues };
    });
  }, []);

  return (
    <>
      <BasicDialog
        closeDialog={() => setIsEditDialogOpen(false)}
        open={isEditDialogOpen}
        title={editData?.id ? t('updateCampaign') : t('createCampaign')}
      >
        <CampaignsForm
          data={editData && editData}
          closeDialog={closeEditDialogAndRefetch}
        />
      </BasicDialog>
      <ConfirmDialog
        text={`Are you sure you want to delete the credentials of "${selectedAdGroupName}"?`}
        title="Delete Ad Group"
        variant="warning"
        onCloseDialog={() => setIsAreYouSureDialogOpen(false)}
        open={isAreYouSureDialogOpen}
        onSubmit={() => {
          if (selectedCampaignId !== null) {
            handleOnDelete(selectedCampaignId);
          }
        }}
        isDisabled={isDeleting}
      />
      <BasicTable
        data={campaignsData?.data}
        refetch={refetchCampaignsData}
        fetchDownload={fetchDownload}
        canPinRows={canPinRows}
        onDelete={(id, item) => handleDeleteClick(id, item?.name, item?.campaignCode)}
        onEdit={(item: CampaignInterface) => setEditData(item)}
        setEditDialogIsOpen={(isOpen) => setIsEditDialogOpen(isOpen)}
        setPage={(newPage) => setPage(newPage)}
        setRowsPerPage={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
        formInputs={campaignFormInputs}
        setSearchInput={setSearchInput}
        handleNavigation={(campaignId) => handleGoToAdGroups(campaignId)}
        extendedColumns={organizationType === OrganizationType.ECOMMERCE ? eCommerceExtendedColumns : extendedColumns}
        setSortBy={setSortBy}
        setOrderBy={setOrderBy}
        filterDateRange={adManagementFilterDateRange}
        setFilterDateRange={setAdManagementFilterDateRange}
        customFilters={(
            <>
            {customFieldSelectData?.data?.data.values &&
              <CampaignCustomFieldSelect
                  data={data}
                  onChange={handleCustomFieldSelectChange}
              />
            }
          <TrackingSourceSelect
            data={selectedTrackingSourceData?.data}
            size="small"
            setSelectedTrackingSourceId={props.setSelectedTrackingSourceId}
            label="Select Tracking Source"
            placeholder="Select Tracking Source"
          />
            </>
                )}
        {...{
          isError, isFetching, isDeleting, page, searchInput, columns, rowsPerPage, sortBy, orderBy, sortablesList,
        }}
      />
    </>
  );
};

export default CampaignsTable;
